/**
 * pushService.js — Web Push subscription helpers for StarTip.
 *
 * Flow:
 *  1. registerAndSubscribe()
 *     a. Wait for SW registration (sw.js is registered in index.js)
 *     b. Ask browser for notification permission
 *     c. Get VAPID public key from backend
 *     d. Call pushManager.subscribe() with the public key
 *     e. POST the subscription to /api/push/subscribe
 *  2. unsubscribe()
 *     a. GET current subscription from pushManager
 *     b. Call pushSubscription.unsubscribe()
 *     c. DELETE /api/push/unsubscribe on backend
 *
 * NOTE: All fetch() calls use window.location.origin so they always resolve
 * to the current domain, regardless of what REACT_APP_BACKEND_URL was set
 * to at build time (avoids stale easy-tip-pay / old-domain issues).
 */

/** Returns the API base URL for fetch() calls — always uses current domain. */
function apiBase() {
  return typeof window !== "undefined"
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "");
}

/** Convert a base64url string to a Uint8Array (required by PushManager.subscribe). */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Check browser support. Returns an object with feature flags. */
export function checkPushSupport() {
  return {
    serviceWorker: "serviceWorker" in navigator,
    pushManager:   "PushManager" in window,
    notification:  "Notification" in window,
    supported:
      "serviceWorker" in navigator &&
      "PushManager"   in window    &&
      "Notification"  in window,
  };
}

/** Returns true if the current device is running iOS/iPadOS. */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Returns true if the app is running as an installed PWA (standalone mode).
 * Uses both navigator.standalone (iOS-specific) and the display-mode media query.
 */
export function isInstalledPWA() {
  return (
    navigator.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

/** Current notification permission state. */
export function getNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

/** Get the VAPID public key from the backend. */
async function fetchVapidPublicKey() {
  const res = await fetch(`${apiBase()}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error("No se pudo obtener la clave VAPID del servidor.");
  const data = await res.json();
  return data.public_key;
}

/**
 * Request notification permission, subscribe to push, and save the subscription
 * on the backend. Returns "granted" | "denied" | "dismissed".
 */
export async function registerAndSubscribe() {
  const support = checkPushSupport();
  if (!support.supported) {
    throw new Error("Tu navegador no soporta notificaciones push.");
  }

  // 1. Request permission
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return permission; // "denied" or "default" (dismissed)
  }

  // 2. Get SW registration
  const registration = await navigator.serviceWorker.ready;

  // 3. Get VAPID public key
  const vapidPublicKey = await fetchVapidPublicKey();
  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

  // 4. Subscribe to push
  let pushSubscription = await registration.pushManager.getSubscription();
  if (!pushSubscription) {
    pushSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  // 5. Send subscription to backend
  const subJSON = pushSubscription.toJSON();
  const res = await fetch(`${apiBase()}/api/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      endpoint:        subJSON.endpoint,
      expiration_time: subJSON.expirationTime ?? null,
      keys: {
        p256dh: subJSON.keys.p256dh,
        auth:   subJSON.keys.auth,
      },
    }),
  });

  if (!res.ok) {
    throw new Error("No se pudo guardar la suscripción en el servidor.");
  }

  return "granted";
}

/** Send a test push notification to the current user (creator must be logged in). */
export async function sendTestPush() {
  const res = await fetch(`${apiBase()}/api/push/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      title: "StarTip — Prueba",
      body:  "Las notificaciones están funcionando correctamente.",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Error al enviar notificación de prueba.");
  }
  return res.json();
}

/** Unsubscribe from push notifications on the CURRENT device only. */
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();

  // Capture endpoint BEFORE unsubscribing from the browser
  const endpoint = sub ? sub.endpoint : null;

  // Remove from browser pushManager
  if (sub) await sub.unsubscribe();

  // Tell backend to deactivate only THIS device's subscription
  await fetch(`${apiBase()}/api/push/unsubscribe`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ endpoint }),
  });
}

/**
 * Silently re-registers the existing push subscription with the backend.
 * Called on every app load when permission is already "granted" to ensure
 * multi-device support: if the backend subscription was deactivated on this
 * device (e.g. by the user toggling off on another device), this restores it.
 *
 * FIX MULTI-DISPOSITIVO: Si la suscripción ya no existe en el navegador
 * (FCM/APNs rotó el token silenciosamente y pushsubscriptionchange no se
 * disparó), crea una nueva automáticamente. Esto evita que un dispositivo
 * quede silencioso mientras otros siguen recibiendo notificaciones.
 *
 * Does NOT request permission — only works when already granted.
 */
export async function silentResubscribe() {
  try {
    if (!checkPushSupport().supported) return;
    if (Notification.permission !== "granted") return;

    const registration = await navigator.serviceWorker.ready;
    let pushSubscription = await registration.pushManager.getSubscription();

    if (!pushSubscription) {
      // La suscripción desapareció (FCM rotó el token, reinstalación de PWA,
      // cambio de VAPID key, etc.). Creamos una nueva automáticamente.
      try {
        const vapidPublicKey = await fetchVapidPublicKey();
        pushSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
      } catch (subErr) {
        // Si falla (e.g. VAPID key inválida), salir silenciosamente.
        return;
      }
    }

    const subJSON = pushSubscription.toJSON();
    await fetch(`${apiBase()}/api/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        endpoint:        subJSON.endpoint,
        expiration_time: subJSON.expirationTime ?? null,
        keys: {
          p256dh: subJSON.keys.p256dh,
          auth:   subJSON.keys.auth,
        },
      }),
    });
  } catch {
    // Silent — never throw, this is a best-effort background call
  }
}

/**
 * Fetch the user's notification preferences from the backend.
 * Returns { tips: boolean, messages: boolean }.
 * Defaults to all-enabled if the request fails (graceful degradation).
 */
export async function getPushPrefs() {
  try {
    const res = await fetch(`${apiBase()}/api/push/prefs`, { credentials: "include" });
    if (!res.ok) return { tips: true, messages: true };
    return res.json();
  } catch {
    return { tips: true, messages: true };
  }
}

/**
 * Save the user's notification preferences to the backend.
 * @param {{ tips: boolean, messages: boolean }} prefs
 */
export async function updatePushPrefs(prefs) {
  const res = await fetch(`${apiBase()}/api/push/prefs`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(prefs),
  });
  if (!res.ok) throw new Error("No se pudieron guardar las preferencias.");
  return res.json();
}
